import Button from "devextreme-react/button";
import DataGrid, { Column, FilterRow, LoadPanel, Paging } from "devextreme-react/data-grid";
import DateBox from "devextreme-react/date-box";
import type DataSource from "devextreme/data/data_source";
import type { ColumnCellTemplateData, DataRowTemplateData } from "devextreme/ui/data_grid";
import React from "react";
import { CommentDto } from "../../../../../dtos/CommentDto";
import { StepDto } from "../../../../../dtos/StepDto";
import { StepStatus } from "../../../../../dtos/StepStatus";
import { TrackedMode } from "../../../../../dtos/TrackedMode";
import TaskActions from "../TaskActions";
import { TasklistMode } from "../../context/TaskListContext";
import { FileLink, FileLinkInfo, FileListHeader } from "../../FileLink";
import LinkifiedText, { LinkifiedHtml } from "../../../LinkifiedText";

export type ITaskSummaryViewProps = {
    gridRef: React.RefObject<any>;
    hideSummary: boolean;

    loadedPhase: { id: string; name: string; startDate?: Date | null; projectedEndDate?: Date | null };

    dataSource: DataSource<StepDto, string>;

    // behavior
    closeSummary: () => void;
    selectAction: () => Promise<void>;

    // helpers
    showResponseInfo: boolean;
    getMostRecentComment: (comments: CommentDto[]) => string | null;
    buildFileLink: (file: FileLinkInfo) => { id: string; name: string; onClick: () => void };

    headerTitle: string;
    grantNumber: string;
    steps?: StepDto[] | null;
    canEditStepDates?: (step: StepDto) => boolean;
    isPhaseTracked?: boolean;
    trackedMode?: TrackedMode;
    onDateChange?: (stepId: string, field: 'startDate' | 'projectedEndDate' | 'submissionDate', value: Date | null) => void;
    minDate?: Date | null;
    maxDate?: Date | null;

    // Workflow flags
    isNoneWorkflow?: boolean;
    isStepWorkflow?: boolean;
    isUploadOnlyTask?: boolean;
    // Whether a subtask's section is description + files (None) or request/response. Per subtask,
    // because a hybrid task mixes the two.
    isNoneStep: (step: StepDto) => boolean;
    mode?: TasklistMode;
    // Content pinned to the right of the summary title (the resubmission cycle picker for Submission
    // phases), mirroring the subtask header's title-left / actions-right layout.
    headerRight?: React.ReactNode;
    // True when the cycle on screen is a closed one - its summary is read-only history.
    isCycleClosed: boolean;
};

export function TaskSummaryView({
    isCycleClosed,
    gridRef,
    hideSummary,
    loadedPhase,
    dataSource,
    closeSummary,
    selectAction,
    showResponseInfo,
    getMostRecentComment,
    buildFileLink,
    headerTitle,
    grantNumber,
    steps,
    canEditStepDates,
    isPhaseTracked = false,
    trackedMode,
    onDateChange,
    minDate,
    maxDate,
    isNoneWorkflow = false,
    isStepWorkflow = false,
    isUploadOnlyTask = false,
    isNoneStep,
    mode = "default",
    headerRight,
}: ITaskSummaryViewProps) {
    if (!loadedPhase) return null;

    const getComment = (comment?: any) => (
        <LinkifiedHtml html={comment?.comment} style={{ whiteSpace: "normal" }} />
    );

    // Template attachments have no upload date, size or owner to show.
    const showFileInfo = mode !== "templates";

    const getFiles = (comment?: any) => {
        const files = (comment?.files ?? []) as FileLinkInfo[];
        if (!files.length) return <div>No files</div>;

        return (
            <>
                {showFileInfo && <div className="file-link-capped"><FileListHeader /></div>}
                {files.map((f) => {
                    const link = buildFileLink(f);
                    return (
                        <div key={link.id} className="file-link-capped">
                            <FileLink file={f} onClick={link.onClick} showInfo={showFileInfo} />
                        </div>
                    );
                })}
            </>
        );
    };

    const getTrackingDates = (step: StepDto) => {
        if (!isPhaseTracked || mode === "templates") return null;

        const isComplete = step.status === StepStatus.APPROVED;
        const canEditProjected = !isCycleClosed && canEditStepDates?.(step) && onDateChange;

        // Find surrounding steps for date constraints
        const stepIndex = steps?.findIndex(s => s.id === step.id) ?? -1;
        const prevStep = stepIndex > 0 ? steps?.[stepIndex - 1] : null;
        const nextStep = stepIndex >= 0 && steps && stepIndex < steps.length - 1 ? steps[stepIndex + 1] : null;

        // Calculate min/max for Projected Start (only used in non-EndDateOnly modes)
        const projectedStartMin = prevStep?.projectedEndDate ? new Date(prevStep.projectedEndDate) : (minDate || undefined);
        const projectedStartMax = step.projectedEndDate ? new Date(step.projectedEndDate) : (maxDate || undefined);

        // Calculate min/max for Projected Completion
        // EndDateOnly mode: use phase-level constraints (cascade logic handles re-sorting)
        // Other modes: use step-based constraints
        const isEndDateOnlyMode = trackedMode === TrackedMode.EndDateOnly;
        const projectedCompletionMin = isEndDateOnlyMode
            ? (minDate || undefined)
            : (step.startDate ? new Date(step.startDate) : (minDate || undefined));
        const projectedCompletionMax = isEndDateOnlyMode
            ? (maxDate || undefined)
            : (nextStep?.startDate ? new Date(nextStep.startDate) : (maxDate || undefined));

        // When complete, show all 4 dates (read-only) - same order as form
        if (isComplete) {
            return (
                <div style={{ display: "flex", gap: "1rem", paddingLeft: "10px", marginBottom: "1rem" }}>
                    <div style={{ flex: "0 0 24%" }}>
                        <h6>Projected Start</h6>
                        <div>{step.startDate ? new Date(step.startDate).toLocaleDateString() : 'Not set'}</div>
                    </div>
                    <div style={{ flex: "0 0 24%" }}>
                        <h6>Projected Completion</h6>
                        <div>{step.projectedEndDate ? new Date(step.projectedEndDate).toLocaleDateString() : 'Not set'}</div>
                    </div>
                    <div style={{ flex: "0 0 24%" }}>
                        <h6>Start Date</h6>
                        <div>{step.submissionDate ? new Date(step.submissionDate).toLocaleDateString() : 'Not set'}</div>
                    </div>
                    <div style={{ flex: "0 0 24%" }}>
                        <h6>Completion Date</h6>
                        <div>{step.completionDate ? new Date(step.completionDate).toLocaleDateString() : 'Not set'}</div>
                    </div>
                </div>
            );
        }

        // When not complete, only show editable projected dates
        // In EndDateOnly mode, Projected Start is calculated automatically and not editable
        const canEditProjectedStart = canEditProjected && trackedMode !== TrackedMode.EndDateOnly;

        return (
            <div style={{ display: "flex", gap: "1rem", paddingLeft: "10px", marginBottom: "1rem" }}>
                <div style={{ flex: "0 0 35%" }}>
                    <h6>Projected Start</h6>
                    {canEditProjectedStart ? (
                        <DateBox
                            value={step.startDate || undefined}
                            onValueChanged={(e) => onDateChange(step.id, 'startDate', e.value)}
                            displayFormat="shortDate"
                            width="100%"
                            min={projectedStartMin}
                            max={projectedStartMax}
                        />
                    ) : (
                        <div>{step.startDate ? new Date(step.startDate).toLocaleDateString() : 'Not set'}</div>
                    )}
                </div>
                <div style={{ flex: "0 0 35%" }}>
                    <h6>Projected Completion</h6>
                    {canEditProjected ? (
                        <DateBox
                            value={step.projectedEndDate || undefined}
                            onValueChanged={(e) => onDateChange(step.id, 'projectedEndDate', e.value)}
                            displayFormat="shortDate"
                            width="100%"
                            min={projectedCompletionMin}
                            max={projectedCompletionMax}
                        />
                    ) : (
                        <div>{step.projectedEndDate ? new Date(step.projectedEndDate).toLocaleDateString() : 'Not set'}</div>
                    )}
                </div>
            </div>
        );
    };

    const getDetail = (step: StepDto) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {getTrackingDates(step)}

            {/* For a None subtask: show description, completion date, and files */}
            {isNoneStep(step) && (
                <>
                    {/* A document store is file-only: no description/completion row */}
                    {!isUploadOnlyTask && (
                        <div style={{ display: "flex", gap: "1rem", paddingLeft: "10px" }}>
                            <div style={{ flex: "0 0 70%" }}>
                                <div style={{ whiteSpace: "normal" }}>
                                    {step.description ? <LinkifiedText text={step.description} /> : "No description provided"}
                                </div>
                            </div>
                            <div style={{ flex: "0 0 30%" }}>
                                {/* Only show completion date here if not tracked+complete (already shown in getTrackingDates) */}
                                {step.completionDate && !(isPhaseTracked && step.status === StepStatus.APPROVED) && (
                                    <div>
                                        Completion Date: {new Date(step.completionDate).toLocaleDateString()}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {/* Files for None workflow - use step.files that are not associated with comments.
                        None aligns files under the completion column (right); Submission has no
                        description/completion row, so files move left and span full width. */}
                    {step.files?.filter(f => !f.commentId).length ? (
                        <div style={{ display: "flex", gap: "1rem", paddingLeft: "10px" }}>
                            {!isUploadOnlyTask && <div style={{ flex: "0 0 70%" }}></div>}
                            <div style={{ flex: isUploadOnlyTask ? "1 1 100%" : "0 0 30%" }}>
                                <h6>Files</h6>
                                {getFiles({ files: step.files.filter(f => !f.commentId) })}
                            </div>
                        </div>
                    ) : null}
                </>
            )}

            {/* For every other subtask: show request and response */}
            {!isNoneStep(step) && (
                <>
                    <div style={{ display: "flex", gap: "1rem", paddingLeft: "10px" }}>
                        <div style={{ flex: "0 0 70%" }}>{getComment(step.requestComment)}</div>
                        <div style={{ flex: "0 0 30%" }}>{getFiles(step.requestComment)}</div>
                    </div>

                    {showResponseInfo && (
                        <div style={{ display: "flex", gap: "1rem", paddingLeft: "10px" }}>
                            <div style={{ flex: '0 0 70%' }}>
                                {step.status == StepStatus.GRANTEE_PENDING_REVISION && (
                                    <div style={{ paddingBottom: "20px" }}>
                                        <h6 style={{ color: "#E91E63" }}>Revision Request</h6>
                                        <LinkifiedText text={getMostRecentComment(step.revisionRequestComments)} />
                                    </div>
                                )}
                                <h6>Response</h6>
                                {getComment(step.responseComment)}
                            </div>
                            <div style={{ flex: "0 0 30%" }}>
                                <h6>Response Files</h6>
                                {getFiles(step.responseComment)}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    const subTaskRender = (e: ColumnCellTemplateData<StepDto, string>) => (
        <div>
            <h5 style={{ backgroundColor: "#e6e6e6", padding: "5px 0 5px 10px" }}>{e.data?.name}</h5>
            {getDetail(e.data as StepDto)}
        </div>
    );

    const renderDetail = (e: DataRowTemplateData<StepDto, string>) => getDetail(e.data);

    const gridEl = (
        <DataGrid<StepDto, string>
            ref={gridRef}
            dataSource={dataSource}
            repaintChangesOnly
            showColumnHeaders={false}
            showColumnLines={false}
        >
            <LoadPanel enabled={false} />
            <Column dataField="id" visible={false} />
            <Column dataField="order" visible={false} sortOrder="asc" />
            <Column caption="Subtask" dataField="name" dataType="string" cellRender={subTaskRender} />
            <FilterRow visible={false} applyFilter="auto" />
            <Paging enabled={false} />
        </DataGrid>
    );

    // No actions on a closed cycle - not disabled, absent. Its phaseActions come from the newest
    // cycle, so leaving them rendered would act on a cycle other than the one on screen.
    const actionsEl = isCycleClosed ? null : (
        <TaskActions
            onSelected={selectAction}
            initialPhase={loadedPhase as any}
            grantNumber={grantNumber}
            steps={steps ?? null}
            editableTaskDates={false}
        />
    );

    return (
        <>
            {!hideSummary && (
                <div className="task-detail-body">
                    {/* Title pinned left, the resubmission cycle picker and close button pinned right -
                        the same layout as the subtask header (ticket 1015). */}
                    <div className="task-main-subtask-header">
                        <div className="task-main-subtask-header-left">
                            <h5 style={{ display: "inline" }} dangerouslySetInnerHTML={{ __html: headerTitle }} />
                        </div>
                        <div className="task-main-subtask-header-right">
                            {headerRight}
                            <Button icon="close" stylingMode="text" onClick={closeSummary} />
                        </div>
                    </div>

                    <div className={`task-main-subtask-form ${isPhaseTracked && mode !== "templates" ? "with-tracking-dates" : ""}`}>
                        {gridEl}
                    </div>

                    <div className="task-main-action-buttons">
                        {actionsEl}
                    </div>
                </div>
            )}
        </>
    );
}
